import { useState, useCallback } from 'react';
import { postsAPI, publishingAPI } from '../api';
import { canDelete, showDeleteConfirmation } from '../utils/postDeleteUtils';
import { showToast, showErrorToast } from '../components/Toast/toastUtils';

/**
 * Shared hook for all post action handlers (edit, publish, delete, compose, raw response).
 * Used by both Scheduler and Posts pages to avoid duplication.
 */
export function usePostActions(refreshCallback) {
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingPostForModal, setEditingPostForModal] = useState(null);
  const [publishingPostId, setPublishingPostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [viewingRawResponsePost, setViewingRawResponsePost] = useState(null);

  const handleComposeClick = useCallback(() => {
    setEditingPost(null);
    setEditingPostForModal(null);
    setIsComposeModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsComposeModalOpen(false);
    setEditingPost(null);
    setEditingPostForModal(null);
  }, []);

  const handleSubmitPost = useCallback(async (postData, postId = null) => {
    try {
      if (postId) {
        await postsAPI.update(postId, postData);
        if (editingPostForModal && editingPostForModal.platform_statuses) {
          const editablePlatforms = Object.entries(editingPostForModal.platform_statuses).filter(
            ([platform, status]) => (
              status && status.status === 'published' && status.post_platform_status_id && platform !== 'threads'
            )
          );
          await Promise.allSettled(
            editablePlatforms.map(([platform, status]) =>
              publishingAPI.editPlatformPost(postId, status.post_platform_status_id, postData.content)
            )
          );
        }
        showToast('Post updated successfully');
      } else {
        await postsAPI.create(postData);
        showToast('Post created successfully');
      }
      if (refreshCallback) await refreshCallback();
    } catch (error) {
      console.error('Error saving post:', error);
      showErrorToast(`Failed to save post: ${error.message || error}`);
      throw error;
    }
  }, [editingPostForModal, refreshCallback]);

  const openEditForPost = useCallback(async (post) => {
    if (!post) return;
    try {
      const fullPost = await postsAPI.getById(post.id);
      setEditingPost(fullPost || post);
      setEditingPostForModal(fullPost || post);
    } catch (error) {
      setEditingPost(post);
      setEditingPostForModal(post);
    }
    setIsComposeModalOpen(true);
  }, []);

  const handlePublishNow = useCallback(async (post) => {
    if (!post) return;
    if (['published', 'publish_pending', 'publish_failed', 'delete_pending', 'deleted', 'delete_failed'].includes(post.status)) {
      showErrorToast('This post is already published and cannot be published again.');
      return;
    }
    const platforms = Object.keys(post.platform_statuses || {});
    if (platforms.length === 0) {
      showErrorToast('Select target platforms before publishing.');
      return;
    }
    setPublishingPostId(post.id);
    try {
      const result = await publishingAPI.publishPost(post.id, platforms);
      const failures = (result.results || []).filter((r) => r.status !== 'published');
      if (failures.length > 0) {
        const details = failures.map((f) => `${f.platform || 'platform'}: ${f.error || 'unknown error'}`).join('\n');
        showErrorToast(`Publish completed with ${failures.length} issue(s):\n${details}`);
      } else {
        showToast('Post published successfully');
      }
      if (refreshCallback) await refreshCallback();
    } catch (error) {
      showErrorToast(`Publish failed: ${error.message || error}`);
    } finally {
      setPublishingPostId(null);
    }
  }, [refreshCallback]);

  const handleDeletePost = useCallback(async (postId, postsList) => {
    const post = (postsList || []).find(p => p.id === postId);
    if (!post) return;
    if (!showDeleteConfirmation(post)) return;
    setDeletingPostId(postId);
    try {
      await postsAPI.delete(postId);
      if (refreshCallback) await refreshCallback();
      showToast('Post deleted successfully');
    } catch (error) {
      console.error('Error deleting post:', error);
      showErrorToast(`Delete failed: ${error.message || error}`);
    } finally {
      setDeletingPostId(null);
    }
  }, [refreshCallback]);

  const handleViewRawResponse = useCallback((post) => {
    const rawResponse = post?.raw_response || (post?.platform_statuses && Object.values(post.platform_statuses).find(ps => ps?.raw_response)?.raw_response);
    setViewingRawResponsePost(rawResponse);
    setShowRawResponse(true);
  }, []);

  return {
    isComposeModalOpen,
    editingPost,
    editingPostForModal,
    publishingPostId,
    deletingPostId,
    showRawResponse,
    viewingRawResponsePost,
    handleComposeClick,
    handleCloseModal,
    handleSubmitPost,
    openEditForPost,
    handlePublishNow,
    handleDeletePost,
    handleViewRawResponse,
    setShowRawResponse,
  };
}
